import filters from "public/json/mapFilters.json";
import { IMapLngLat } from "~/types/map.interface";

export type PredefinedAABB = {
  [city: string]: {
    aabb: {
      min: number[],
      max: number[]
    }
  }
};

export type CountryCode = "cy" | "lt" | "by";

export type WorkingStatus = {
    isOpenNow: boolean,
    isClosedToday: boolean,
    todayWorkingTime: string,
}

export interface ILngLat {
  long: number;
  lat: number;
}

export type LimitationType = "none" | "partial" | "full";
export enum LimitationTypeEnum {
  NONE = "none",
  PARTIAL = "partial",
  FULL = "full",
}

export type SubscriptionModel = "archived" | "default";

export type AvailableService = {
  name: string;
  isLimited: boolean;
};

export interface ILevel {
  id: string;
  value: string;
  isArchived: boolean;
  activities: string[];
  availableServices: AvailableService[];
  limitationType: LimitationType;
}

export interface ISupplier {
  id: number | string;
  country: string;
  name: string;
  description: string;
  address: string;
  phones: string[];
  url: string | null;
  rulesUrl: string | null;
  rulesText: string | null;
  imageUrls: string[];
  levels: ILevel[];
  tags: string[];
  workingTime: {
    mon: string;
    tue: string;
    wed: string;
    thu: string;
    fri: string;
    sat: string;
    sun: string;
  };
  originalWorkingTimeSource: string;
  workingTimeUpdatedAt: string | null;
  coordinates: number[];
  city: string;
  range: null | number;
}

export interface IBaseFilter {
  id: string;
  value: string;
}

export type SeparatedLevels = { archived: ILevelFilter[], default: ILevelFilter[] };
export type SeparatedBaseLevel = { archived: IBaseFilter, default: IBaseFilter };

export interface ILevelFilter extends IBaseFilter {
  isAvailable: boolean;
  reason: string;
}

interface ICountry extends IBaseFilter {
  bounds:
    | {
        portrait: [IMapLngLat, IMapLngLat];
        landscape: [IMapLngLat, IMapLngLat];
      }
    | any;
}

// interface for public/json/mapFilters.json
export interface IMapFilter {
  countries: IBaseFilter[];
  cities: City[];
  defaultLevel: {
    archived: IBaseFilter;
    default: IBaseFilter;
  };
  levels: SeparatedLevels;
  defaultLocation: IBaseFilter;
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  zoom: number | [];
  bounds: IMapLngLat;
}

export interface IStatistics {
  facility_amount: number,
  activity_amount: number
}

export type ILevelStatistics = {
  [key: string]: IStatistics;
};

// interface for public/json/statistics.json
export interface IStatisticJSON {
  statistics: IStatistics;
  levels: ILevelStatistics;
  archived_levels: ILevelStatistics,
}

export interface IDefaultFilter {
  bounds: IMapLngLat;
  city: IBaseFilter;
  level: IBaseFilter;
  zoom: number | [];
}

// interface for api/map/filters.post.ts
export interface IFiltersPost {
  country: ICountry;
  cities: ILevelFilter[];
  levels: ILevelFilter[];
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  default: IDefaultFilter;
}

// interface for api/map/v2/filters.post.ts
export interface IFiltersPostV2 {
  country: ICountry;
  cities: ILevelFilter[];
  levels: SeparatedLevels;
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  bounds: IMapLngLat;
  city: IBaseFilter;
  zoom: number | [];
  defaultLevel: {
    archived: ILevelFilter,
    default: ILevelFilter,
  };
}

export type ICityLevelFilterJSON = {
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  zoom: number | [];
  bounds: IMapLngLat | [];
  default_level: IBaseFilter;
  default_location: IBaseFilter;
}

// interface for archived and default objects under JSON downloaded from http://testjournal.allsport.by/storage/map_filter_v2.zip
export interface ICityLevelFiltersJSON {
  [key: string]: ICityLevelFilterJSON;
}

export type ICityLevelFilter = {
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  zoom: number | [];
  bounds: IMapLngLat | [];
  defaultLevel: IBaseFilter;
  defaultLocation: IBaseFilter;
}

// interface for public/json/cityLevelFilters.json
export interface ICombinedCityLevelFilters {
  archived: {
    [key: string]: ICityLevelFilter;
  },
  default: {
    [key: string]: ICityLevelFilter;
  },
}

// interface for JSON downloaded from http://testjournal.allsport.by/storage/map_filter_v2.zip
export interface IMapFilterJSON {
  countries: IBaseFilter[];
  cities: City[];
  default_level: {
    archived: IBaseFilter;
    default: IBaseFilter;
  };
  levels: {
    archived: ILevelFilter[];
    default: ILevelFilter[];
  }
  default_location: IBaseFilter;
  activities: ILevelFilter[];
  tags: IBaseFilter[];
  zoom: number | [];
  bounds: IMapLngLat | [];
  default?: ICityLevelFiltersJSON;
  archived?: ICityLevelFiltersJSON;
}

export interface IFilterActivities {
  [key: string]: {
    activities: ILevelFilter[];
  };
}

export interface IFilterActivitiesJSON {
  archived: IFilterActivities,
  default: IFilterActivities,
}

export interface IFilterWithCitiesValue extends IBaseFilter {
  cities: ICombinedLevelFilter[];
}

export interface ICombinedLevelFilter extends City {
  isAvailable: boolean;
  reason: string;
}

export interface IFilterWithLevelsValue extends City {
  levels: {
    archived: ILevelFilter[];
    default: ILevelFilter[];
  };
}

export interface ICombinedLevelsWithCities {
  archived: IFilterWithCitiesValue[];
  default: IFilterWithCitiesValue[];
}

export interface IFilterLevelsJSON {
  cities: IFilterWithLevelsValue[];
  levels: ICombinedLevelsWithCities;
}

export interface City {
  id: string;
  value: string;
  aabb: { min: number[]; max: number[] };
}

export interface ISupplierSearch {
  id: string | number;
  name: string;
  address: string;
  activities: string[];
  levels: string[];
  imageUrl: string;
  coordinates: number[];
  rate: number;
  range: number | null;
}

export interface ISearchResult {
  searchList: ISupplierSearch[];
  searchListFromOtherCities?: ISupplierSearch[];
}

type SetupLevelSearch = {
  search: string[];
}

export interface ISearchToGenerate {
  mainSearch: string[];
  searchByLevels: {
    [level: string]: SetupLevelSearch;
  };
}

export interface IGeneratedSearch {
  mainSearch: string;
  searchByLevels: {
    [level: string]: string;
  };
}

export interface IGeneratedSearchJSON {
  [key: string]: IGeneratedSearch;
}

export type CountryCodes = keyof typeof filters;

export type Facility = {
  city: string;
  objects: PreparedSupplier[];
};

export type TableFilters = {
  country: CountryCodes;
  levels: SeparatedLevels;
  cities: ILevelFilter[];
  activities: ILevelFilter[];
  tags: ILevelFilter[];
};

export type Day = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
export type WorkingTime = Readonly<Partial<Record<Day, string>>>;

export interface PreparedSupplier {
  id: number | string;
  name: string;
  levels: ILevel[];
  city: string;
  activities: string[];
  services: AvailableService[];
  range: number | null;
  workingTime: WorkingTime;
}

export type ISetupJsonSuppliersSearchOptions = {
  fatal: boolean;
  isSportbenefitBrand: boolean;
};